import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppConfig } from '../app.config';
import { LanguageChangeComponent } from '@shared/bottom-sheet-layouts/language-change/language-change.component';
import { CsIconModule } from '@components/icon';

type LanguageDetails = { code: string; displayName: string };

@Component({
  selector: 'change-language-page',
  templateUrl: './change-language.html',
  standalone: true,
  imports: [CsIconModule],
})
export class ChangeLanguagePageComponent implements OnInit {
  private readonly conn = inject(ConnectionService);
  private readonly broadcast = inject(BroadcastService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly appConfig = inject(AppConfig);

  appLanguage = signal<LanguageDetails | undefined>(undefined);
  communicationLanguage = signal<LanguageDetails | undefined>(undefined);
  showUpdatedBadge = signal(false);
  showCommLangUpdatedBadge = signal(false);

  isAppLanguageEnglish = computed(
    () => this.appLanguage()?.code === this.appConfig.Shared.Languages.EN,
  );

  isAllowedCommunicationLanguage = computed(() => {
    const allowed = [
      this.appConfig.Shared.Languages.EN,
      this.appConfig.Shared.Languages.HI,
      this.appConfig.Shared.Languages.TE,
      this.appConfig.Shared.Languages.MR,
    ];
    return allowed.includes(this.appLanguage()?.code);
  });

  ngOnInit(): void {
    const user = this.conn.getActingUser();
    const appLangCode = user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
    const commLangCode = user?.get('communicationLanguagePreference') || this.appConfig.Shared.Languages.EN;

    this.appLanguage.set({
      code: appLangCode,
      displayName: this.appConfig.Shared.LanguagesLong[appLangCode.toUpperCase()],
    });
    this.communicationLanguage.set({
      code: commLangCode,
      displayName: this.appConfig.Shared.LanguagesLong[commLangCode.toUpperCase()],
    });

    if (sessionStorage.getItem('langJustChanged')) {
      this.showUpdatedBadge.set(true);
      sessionStorage.removeItem('langJustChanged');
    }
  }

  openAppLanguageSheet(): void {
    this.bottomSheet.open(LanguageChangeComponent, {
      data: {
        translateChatMessages: false,
        currentLanguageCode: this.appLanguage()?.code,
      },
    });
  }

  openCommunicationLanguageSheet(): void {
    const ref = this.bottomSheet.open(LanguageChangeComponent, {
      data: {
        translateChatMessages: false,
        skipNavigation: true,
        isCommunicationLanguage: true,
        currentLanguageCode: this.communicationLanguage()?.code,
        allowedLanguageCodes: [
          this.appConfig.Shared.Languages.EN,
          this.appConfig.Shared.Languages.HI,
          this.appConfig.Shared.Languages.TE,
          this.appConfig.Shared.Languages.MR,
        ],
      },
    });
    ref.afterDismissed().subscribe((lang: LanguageDetails) => {
      if (lang) {
        this.communicationLanguage.set(lang);
        this.showCommLangUpdatedBadge.set(true);
      }
    });
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }
}
